// Missions Noticeboard Feed Component
import type { Mission } from '../types';
import { getCurrentProfile } from '../services/authService';
import { getZipDistance } from '../utils/zipUtils';

export interface MissionFeedFilters {
  type: string;
  kidPolicy: string;
  startDate?: string;
  endDate?: string;
  search: string;
  zipCode?: string;
  radiusMiles?: number;
}

export function renderMissionFeed(
  allMissions: Mission[],
  filteredMissions: Mission[],
  filters: MissionFeedFilters
): string {
  const profile = getCurrentProfile();
  const currentUid = profile?.uid || '';
  const currentZip = filters.zipCode || '';
  const currentRadius = filters.radiusMiles ?? 50;

  // Count active missions per type (excluding removed or unlisted for others)
  const visibleMissions = allMissions.filter(m => m.status !== 'removed' && (m.status !== 'under_review' || m.host.userId === currentUid));
  const counts = {
    all: visibleMissions.length,
    project: visibleMissions.filter(m => m.type === 'project').length,
    social: visibleMissions.filter(m => m.type === 'social').length,
    volunteer: visibleMissions.filter(m => m.type === 'volunteer').length,
    sports: visibleMissions.filter(m => m.type === 'sports').length,
    gaming: visibleMissions.filter(m => m.type === 'gaming' || m.type === 'sports_gaming').length
  };

  return `
    <div class="space-y-6 animate-fade-in font-sans">
      
      <!-- Tactical Hero Noticeboard Banner -->
      <section class="card-workshop p-6 sm:p-7 relative overflow-hidden bg-[#090b12] border-white/[0.08]">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-5 relative z-10">
          <div class="space-y-2 max-w-2xl">
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-transparent text-cyan-400 border border-cyan-400/50 shadow-[0_0_8px_rgba(0,240,255,0.2)]">
                Local Noticeboard
              </span>
              <span class="text-xs text-slate-400">Adult friendship through shared practical missions</span>
            </div>
            <h1 class="text-lg sm:text-xl font-black text-slate-100 tracking-tight">
              Got a big job, an itch to build, or want to fire up the smoker?
            </h1>
            <p class="text-xs sm:text-sm text-slate-300 max-w-2xl">
              Dads don't connect over awkward coffee chats. We bond side by side as we lend a hand, turn wrenches, share a game, or come to the rescue. Join an upcoming project or rally your fellow dads.
            </p>
          </div>
          <div class="flex items-center gap-2 flex-wrap">
            <button id="banner-filter-project-btn" class="px-3.5 py-2 rounded-xl bg-transparent border border-amber-400/60 text-amber-300 text-xs font-bold hover:border-amber-400 hover:text-white transition-colors flex items-center gap-1.5 shadow-[0_0_10px_rgba(255,183,3,0.2)] cursor-pointer">
              <i class="fa-solid fa-hammer text-amber-400"></i> Browse Projects
            </button>
            <button id="banner-open-mayday-btn" class="px-3.5 py-2 rounded-xl bg-transparent border border-pink-500/80 text-pink-400 text-xs font-bold hover:border-pink-400 hover:text-white transition-colors flex items-center gap-1.5 shadow-[0_0_12px_rgba(255,42,133,0.35)] mayday-pulse cursor-pointer">
              <i class="fa-solid fa-triangle-exclamation text-pink-400"></i> Send Mayday Call
            </button>
          </div>
        </div>
      </section>

      <!-- Advanced Filter & Search Bar -->
      <div class="bg-[#0a0c13] border border-white/[0.08] rounded-2xl p-4 shadow-sm space-y-3 relative">
        <div class="laser-line absolute top-0 left-0 right-0 opacity-20"></div>
        
        <!-- Category Buttons -->
        <div class="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          <button data-type-filter="all" class="feed-filter-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${filters.type === 'all' ? 'bg-transparent text-cyan-300 border border-cyan-400 shadow-[0_0_10px_rgba(0,240,255,0.3)]' : 'bg-transparent text-slate-400 hover:text-white border border-white/[0.08] hover:border-white/20'}">
            All Missions <span class="border border-white/10 px-1.5 rounded-full text-[10px]">${counts.all}</span>
          </button>
          <button data-type-filter="project" class="feed-filter-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${filters.type === 'project' ? 'bg-transparent text-amber-300 border border-amber-400 shadow-[0_0_10px_rgba(255,183,3,0.3)]' : 'bg-transparent text-slate-400 hover:text-white border border-white/[0.08] hover:border-white/20'}">
            <i class="fa-solid fa-hammer text-amber-400"></i> Projects <span class="border border-white/10 px-1.5 rounded-full text-[10px]">${counts.project}</span>
          </button>
          <button data-type-filter="social" class="feed-filter-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${filters.type === 'social' ? 'bg-transparent text-cyan-300 border border-cyan-400 shadow-[0_0_10px_rgba(0,240,255,0.3)]' : 'bg-transparent text-slate-400 hover:text-white border border-white/[0.08] hover:border-white/20'}">
            <i class="fa-solid fa-burger text-cyan-400"></i> Socials <span class="border border-white/10 px-1.5 rounded-full text-[10px]">${counts.social}</span>
          </button>
          <button data-type-filter="volunteer" class="feed-filter-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${filters.type === 'volunteer' ? 'bg-transparent text-emerald-300 border border-emerald-400 shadow-[0_0_10px_rgba(0,255,136,0.3)]' : 'bg-transparent text-slate-400 hover:text-white border border-white/[0.08] hover:border-white/20'}">
            <i class="fa-solid fa-handshake-angle text-emerald-400"></i> Volunteering <span class="border border-white/10 px-1.5 rounded-full text-[10px]">${counts.volunteer}</span>
          </button>
          <button data-type-filter="sports" class="feed-filter-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${filters.type === 'sports' ? 'bg-transparent text-sky-300 border border-sky-400 shadow-[0_0_10px_rgba(56,189,248,0.3)]' : 'bg-transparent text-slate-400 hover:text-white border border-white/[0.08] hover:border-white/20'}">
            <i class="fa-solid fa-basketball text-sky-400"></i> Sports <span class="border border-white/10 px-1.5 rounded-full text-[10px]">${counts.sports}</span>
          </button>
          <button data-type-filter="gaming" class="feed-filter-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${filters.type === 'gaming' ? 'bg-transparent text-purple-300 border border-purple-400 shadow-[0_0_10px_rgba(168,85,247,0.3)]' : 'bg-transparent text-slate-400 hover:text-white border border-white/[0.08] hover:border-white/20'}">
            <i class="fa-solid fa-gamepad text-purple-400"></i> Gaming <span class="border border-white/10 px-1.5 rounded-full text-[10px]">${counts.gaming}</span>
          </button>
        </div>

        <!-- Filter Controls Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 pt-1 border-t border-white/[0.06] text-xs">
          
          <!-- Free Text Search across Titles and Descriptions -->
          <div class="relative">
            <i class="fa-solid fa-magnifying-glass absolute left-3 top-2.5 text-slate-500 text-xs"></i>
            <input 
              type="text" 
              id="feed-search-input" 
              value="${filters.search}" 
              placeholder="Search title & description..." 
              class="w-full bg-[#06070a] border border-white/[0.1] text-slate-200 text-xs rounded-lg pl-8 pr-3 py-1.5 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)]"
            >
          </div>

          <!-- Kid Policy Filter -->
          <div>
            <select id="feed-kid-select" class="w-full bg-[#06070a] border border-white/[0.1] text-slate-300 text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-cyan-400 cursor-pointer">
              <option value="all" ${filters.kidPolicy === 'all' ? 'selected' : ''}>Any Kid Policy</option>
              <option value="dads_only" ${filters.kidPolicy === 'dads_only' ? 'selected' : ''}>Dads Only (Tools Present)</option>
              <option value="kids_welcome" ${filters.kidPolicy === 'kids_welcome' ? 'selected' : ''}>With Kids (Kids Welcome)</option>
              <option value="family_all" ${filters.kidPolicy === 'family_all' ? 'selected' : ''}>Family Friendly (Entire Family)</option>
            </select>
          </div>

          <!-- ZIP Code & Radius Distance Filter (Default within 50 miles) -->
          <div class="flex items-center gap-1.5">
            <div class="relative w-28 flex-shrink-0">
              <input 
                type="text" 
                id="feed-zip-input" 
                maxlength="5" 
                value="${currentZip}" 
                placeholder="ZIP Code" 
                class="w-full bg-[#06070a] border border-white/[0.1] text-slate-200 text-xs rounded-lg px-2.5 py-1.5 text-center font-bold focus:border-cyan-400"
              >
            </div>
            <select id="feed-radius-select" class="flex-1 bg-[#06070a] border border-white/[0.1] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-cyan-400 cursor-pointer">
              <option value="10" ${currentRadius === 10 ? 'selected' : ''}>Within 10 mi</option>
              <option value="25" ${currentRadius === 25 ? 'selected' : ''}>Within 25 mi</option>
              <option value="50" ${currentRadius === 50 ? 'selected' : ''}>Within 50 mi (Default)</option>
              <option value="100" ${currentRadius === 100 ? 'selected' : ''}>Within 100 mi</option>
              <option value="9999" ${currentRadius >= 9999 ? 'selected' : ''}>Anywhere</option>
            </select>
          </div>

          <!-- Date Range Filter -->
          <div class="flex items-center gap-1.5">
            <input 
              type="date" 
              id="feed-start-date" 
              value="${filters.startDate || ''}" 
              title="Start Date"
              class="w-1/2 bg-[#06070a] border border-white/[0.1] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:border-cyan-400 cursor-pointer"
            >
            <span class="text-slate-500 text-[10px]">&ndash;</span>
            <input 
              type="date" 
              id="feed-end-date" 
              value="${filters.endDate || ''}" 
              title="End Date"
              class="w-1/2 bg-[#06070a] border border-white/[0.1] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:border-cyan-400 cursor-pointer"
            >
          </div>

        </div>

        ${(filters.type !== 'all' || filters.kidPolicy !== 'all' || filters.search || filters.zipCode || filters.startDate || filters.endDate) ? `
          <div class="flex items-center justify-between pt-1 border-t border-white/[0.06] text-[11px]">
            <span class="text-slate-400">
              Showing ${filteredMissions.length} of ${allMissions.length} events
              ${currentZip ? `near <strong>${currentZip}</strong> (${currentRadius >= 9999 ? 'Anywhere' : `${currentRadius} miles`})` : ''}
            </span>
            <button id="reset-filters-btn" class="text-cyan-400 hover:underline font-semibold cursor-pointer">
              Clear All Filters
            </button>
          </div>
        ` : ''}

      </div>

      <!-- Missions Cards Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        ${filteredMissions.length === 0 ? `
          <div class="col-span-full py-12 text-center bg-[#0a0c13] border border-white/[0.08] rounded-2xl p-6">
            <i class="fa-solid fa-person-digging text-slate-600 text-4xl mb-3"></i>
            <h4 class="text-base font-bold text-slate-300">No events match your current filters</h4>
            <p class="text-xs text-slate-500 mt-1 max-w-sm mx-auto">Try clearing your filters or widening your distance radius to discover events in neighboring areas.</p>
            <button id="reset-empty-filters-btn" class="mt-4 px-4 py-2 bg-transparent hover:bg-white/[0.04] text-cyan-400 rounded-lg text-xs font-bold border border-cyan-400/50 shadow-[0_0_10px_rgba(0,240,255,0.2)] cursor-pointer">
              Reset Filters
            </button>
          </div>
        ` : filteredMissions.map(evt => {
    let badgeColor = 'badge-project';
    let icon = 'fa-hammer';
    let typeLabel = 'Project';

    if (evt.type === 'social') {
      badgeColor = 'badge-social';
      icon = 'fa-burger';
      typeLabel = 'Social';
    } else if (evt.type === 'volunteer') {
      badgeColor = 'badge-volunteer';
      icon = 'fa-handshake-angle';
      typeLabel = 'Volunteering';
    } else if (evt.type === 'sports') {
      badgeColor = 'badge-sports';
      icon = 'fa-basketball';
      typeLabel = 'Sports';
    } else if (evt.type === 'gaming' || evt.type === 'sports_gaming') {
      badgeColor = 'badge-gaming';
      icon = 'fa-gamepad';
      typeLabel = 'Gaming';
    }

    const slotsLeft = evt.maxSlots - evt.enlisted.length;
    const isUserEnlisted = evt.enlisted.some(e => e.userId === currentUid);
    const isCreator = currentUid && evt.host.userId === currentUid;
    const isUnderReview = evt.status === 'under_review';

    // Distance calculation if user entered a zip
    let distanceStr = '';
    if (currentZip && /^\d{5}$/.test(currentZip) && evt.zipCode) {
      const dist = getZipDistance(currentZip, evt.zipCode);
      if (dist !== null) {
        distanceStr = `${dist} mi away`;
      }
    }

    return `
            <div class="card-workshop p-5 flex flex-col justify-between group relative ${isUnderReview ? 'border-amber-500/50 bg-[#120f0a]' : ''}">
              <div class="space-y-3">
                
                <!-- Status & Archetype Row -->
                <div class="flex items-center justify-between gap-2 flex-wrap">
                  <div class="flex items-center gap-1.5">
                    <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-bold ${badgeColor}">
                      <i class="fa-solid ${icon}"></i> ${typeLabel}
                    </span>
                    ${evt.isTentative ? `
                      <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-950/60 border border-amber-400/50 text-amber-300">
                        Tentative
                      </span>
                    ` : ''}
                    ${isUnderReview ? `
                      <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 border border-amber-400 text-amber-300 flex items-center gap-1">
                        <i class="fa-solid fa-triangle-exclamation"></i> Under Review
                      </span>
                    ` : ''}
                  </div>
                  
                  <div class="flex items-center gap-2">
                    ${distanceStr ? `
                      <span class="text-[10px] font-bold text-cyan-400 flex items-center gap-1 bg-cyan-950/40 border border-cyan-400/30 px-2 py-0.5 rounded-full">
                        <i class="fa-solid fa-location-arrow text-[9px]"></i> ${distanceStr}
                      </span>
                    ` : ''}
                    <span class="text-[11px] font-semibold text-slate-400 flex items-center gap-1">
                      <i class="fa-regular fa-clock text-slate-500"></i> ${slotsLeft > 0 ? `${slotsLeft} slot${slotsLeft > 1 ? 's' : ''} left` : 'Full Crew'}
                    </span>
                  </div>
                </div>

                <!-- Title -->
                <h3 class="text-base font-bold text-slate-100 group-hover:text-cyan-300 transition-colors line-clamp-2 leading-snug">
                  ${evt.title}
                </h3>

                <!-- Time & Location -->
                <div class="space-y-1 text-xs text-slate-400">
                  <div class="flex items-center gap-2">
                    <i class="fa-regular fa-calendar text-cyan-400 w-3.5"></i>
                    <span class="truncate">${evt.datetime}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    ${isUserEnlisted ? `
                      <i class="fa-solid fa-lock-open text-emerald-400 w-3.5"></i>
                      <span class="text-emerald-300 font-bold truncate">${evt.exactAddress}</span>
                    ` : `
                      <i class="fa-solid fa-location-dot text-slate-500 w-3.5"></i>
                      <span class="truncate">${evt.locationMasked}</span>
                      ${evt.zipCode ? `<span class="text-slate-400 font-semibold text-[11px]">(${evt.zipCode})</span>` : ''}
                    `}
                  </div>
                </div>

                <!-- Description -->
                <p class="text-xs text-slate-300 line-clamp-3 leading-relaxed">
                  ${evt.description}
                </p>

                <!-- Kid Policy Badge -->
                <div class="pt-1">
                  <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[10px] font-medium bg-transparent border border-white/[0.12] text-slate-300">
                    <i class="fa-solid ${evt.kidPolicy === 'dads_only' ? 'fa-shield-halved text-amber-400' : 'fa-child text-cyan-400'}"></i>
                    ${evt.kidPolicyText}
                  </span>
                </div>

                <!-- Skills Needed -->
                ${evt.skillsNeeded && evt.skillsNeeded.length > 0 ? `
                  <div class="flex flex-wrap gap-1 pt-1">
                    ${evt.skillsNeeded.slice(0, 2).map(skill => `
                      <span class="text-[10px] px-2 py-0.5 rounded bg-transparent text-slate-400 border border-white/[0.08]">
                        ${skill}
                      </span>
                    `).join('')}
                    ${evt.skillsNeeded.length > 2 ? `<span class="text-[10px] text-slate-500 pt-0.5">+${evt.skillsNeeded.length - 2} more</span>` : ''}
                  </div>
                ` : ''}

              </div>

              <!-- Footer: Host & Action Button -->
              <div class="pt-4 mt-4 border-t border-white/[0.08] flex items-center justify-between gap-3">
                <button class="view-dad-profile-btn flex items-center gap-2 text-left group/host cursor-pointer hover:opacity-90" data-dad-uid="${evt.host.userId}" title="View Dad Resume">
                  <div class="w-8 h-8 rounded-lg bg-transparent text-cyan-400 border border-cyan-400/40 group-hover/host:border-cyan-400 flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-[0_0_8px_rgba(0,240,255,0.2)]">
                    ${evt.host.avatarInitials}
                  </div>
                  <div class="text-[11px] leading-tight">
                    <span class="text-slate-300 font-semibold block truncate max-w-[110px] sm:max-w-none group-hover/host:text-cyan-300 group-hover/host:underline">${evt.host.name}</span>
                    <span class="text-[10px] text-cyan-400 font-medium">Event Host</span>
                  </div>
                </button>

                <div class="flex items-center gap-1.5">
                  ${isUserEnlisted ? `
                    <span class="px-2.5 py-1 rounded-lg bg-transparent text-emerald-400 border border-emerald-400/60 text-[11px] font-bold flex items-center gap-1 shadow-[0_0_8px_rgba(0,255,136,0.25)]">
                      <i class="fa-solid fa-check text-emerald-400"></i> Enlisted
                    </span>
                  ` : ''}
                  <button data-event-id="${evt.id}" class="mission-details-btn btn-secondary px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer">
                    Details
                  </button>
                  <button data-report-event-id="${evt.id}" class="report-event-btn text-slate-500 hover:text-amber-400 p-1.5 text-xs transition-colors cursor-pointer" title="Report concern">
                    <i class="fa-solid fa-flag text-[11px]"></i>
                  </button>
                </div>
              </div>

            </div>
          `;
  }).join('')}
      </div>

    </div>
  `;
}

export function bindMissionFeedEvents(
  onFilterChange: (filters: Partial<MissionFeedFilters>) => void,
  onOpenDetails: (eventId: string) => void,
  onOpenMaydayModal: () => void,
  onViewDadProfile?: (dadUid: string) => void,
  onReportConcern?: (mission: Mission) => void,
  allMissions: Mission[] = []
): void {
  // Category filter buttons
  document.querySelectorAll('.feed-filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const type = (btn as HTMLElement).dataset.typeFilter || 'all';
      onFilterChange({ type });
    });
  });

  // Free text search input
  const searchInput = document.getElementById('feed-search-input') as HTMLInputElement;
  searchInput?.addEventListener('input', () => {
    onFilterChange({ search: searchInput.value });
  });

  // Kid policy dropdown
  const kidSelect = document.getElementById('feed-kid-select') as HTMLSelectElement;
  kidSelect?.addEventListener('change', () => {
    onFilterChange({ kidPolicy: kidSelect.value });
  });

  // ZIP Code input
  const zipInput = document.getElementById('feed-zip-input') as HTMLInputElement;
  zipInput?.addEventListener('input', () => {
    const val = zipInput.value.trim();
    if (val.length === 5 || val.length === 0) {
      onFilterChange({ zipCode: val });
    }
  });

  // Distance Radius dropdown
  const radiusSelect = document.getElementById('feed-radius-select') as HTMLSelectElement;
  radiusSelect?.addEventListener('change', () => {
    onFilterChange({ radiusMiles: parseInt(radiusSelect.value, 10) || 50 });
  });

  // Date range filters
  const startDateInput = document.getElementById('feed-start-date') as HTMLInputElement;
  startDateInput?.addEventListener('change', () => {
    onFilterChange({ startDate: startDateInput.value || undefined });
  });

  const endDateInput = document.getElementById('feed-end-date') as HTMLInputElement;
  endDateInput?.addEventListener('change', () => {
    onFilterChange({ endDate: endDateInput.value || undefined });
  });

  // Clear filters
  const resetAll = () => {
    onFilterChange({
      type: 'all',
      kidPolicy: 'all',
      search: '',
      zipCode: '',
      radiusMiles: 50,
      startDate: undefined,
      endDate: undefined
    });
  };
  document.getElementById('reset-filters-btn')?.addEventListener('click', resetAll);
  document.getElementById('reset-empty-filters-btn')?.addEventListener('click', resetAll);

  // Banner shortcut buttons
  document.getElementById('banner-filter-project-btn')?.addEventListener('click', () => {
    onFilterChange({ type: 'project' });
  });
  document.getElementById('banner-open-mayday-btn')?.addEventListener('click', () => {
    onOpenMaydayModal();
  });

  // Dad Profile Click Trigger on Host
  document.querySelectorAll('.view-dad-profile-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const dadUid = (btn as HTMLElement).dataset.dadUid;
      if (dadUid && onViewDadProfile) {
        onViewDadProfile(dadUid);
      }
    });
  });

  // Mission details buttons
  document.querySelectorAll('.mission-details-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const eventId = (btn as HTMLElement).dataset.eventId;
      if (eventId) {
        onOpenDetails(eventId);
      }
    });
  });

  // Report concern buttons on cards
  document.querySelectorAll('.report-event-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const eventId = (btn as HTMLElement).dataset.reportEventId;
      const targetMission = allMissions.find(m => m.id === eventId);
      if (targetMission && onReportConcern) {
        onReportConcern(targetMission);
      }
    });
  });
}
